import { GridApi } from '@ag-grid-community/core'
import { Injectable, Type } from '@angular/core'
import { ValidatorFn } from '@angular/forms'
import { HotkeyDestroyFn, HotkeysService } from '@helios/hotkeys'
import { RootState, selectFocusedUIKey } from '@helios/state-management'
import { ModalPositionService } from '@helios/user-preferences'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { FilterModalComponent } from '../../components/filter-modal/filter-modal.component'
import { AdvancedOptionsParams } from '../../models/advanced-options.model'
import {
  ColumnFilterModel,
  FilterEditorModel,
} from '../../models/filter-editor.model'
import { FilterModalEvent } from '../../models/filter-modal-events.model'
import { FilterModel } from '../../models/filter.model'
import { getColumnFilterModels } from '../../utils/column-filter-utils'

type AdvancedOptionsParamsFunc = () => AdvancedOptionsParams

/**
 * Usage:
 *
 * Since the filter modal service shouldn't have knowledge of the other
 * panels or grids, this is scoped to specifically the grid component.
 *
 * Example:
 *
 * @ Component({
 * ...
 *  providers: [FilterModalService]
 * })
 */
@Injectable()
export class FilterModalService {
  /** @internal */
  _focusedUIKey?: string
  currentPanelKey?: string
  currentGridKey?: string
  gridApi?: GridApi
  omitColumns: string[] = []
  advancedOptions?: Type<unknown>
  advancedOptionsParamsFunc?: AdvancedOptionsParamsFunc
  isCacheLoadedFunc?: () => boolean

  static isHotkeyRegistered = false

  private readonly destroySubject$ = new Subject<void>()
  /** @internal */
  _isModalLaunched = false

  onkeyPressBinding = this.onKeyPress.bind(this)
  events = new ReplaySubject<FilterModalEvent>(1)
  hotkeysDestroyFn?: HotkeyDestroyFn

  isClearFiltersEnabled = true
  isResetFiltersEnabled = false

  onClearFilters = new Subject<void>()
  onResetFilters = new Subject<void>()

  isOmitColumnRemoved = false

  validators: { [field: string]: ValidatorFn } = {}

  constructor(
    private readonly modalService: NgbModal,
    private readonly store: Store<RootState>,
    private readonly hotkeys: HotkeysService<'filter-modal'>,
    readonly modalPositionService: ModalPositionService,
  ) {
    this.store
      .select(selectFocusedUIKey)
      .pipe(distinctUntilChanged(), takeUntil(this.destroySubject$))
      .subscribe(focused => (this._focusedUIKey = focused))

    // We need to bind to the global keypress event, instead of the grid keypress event
    document.addEventListener('keypress', this.onkeyPressBinding)

    this.registerHotkeys()
  }

  registerHotkeys() {
    if (FilterModalService.isHotkeyRegistered) {
      return
    }

    this.hotkeys.register('filter-modal', {
      key: ';',
      description: 'Open Filter Modal',
      name: 'Filter Modal',
    })

    this.hotkeysDestroyFn = this.hotkeys.bind('filter-modal', () => {
      // Ignore this binding for now
    })

    FilterModalService.isHotkeyRegistered = true
  }

  /**
   * Since multiple grids will be registered, we'll need to put into a map
   */
  registerGrid(
    grid: GridApi,
    panelKey: string,
    gridKey: string,
    omitColumns: string[] = [],
    advancedOptions?: Type<unknown>,
    advancedOptionsParams?: AdvancedOptionsParamsFunc,
    isCacheLoadedFunc: () => boolean = () => true,
  ): Observable<FilterModalEvent> {
    this.gridApi = grid
    this.currentPanelKey = panelKey
    this.currentGridKey = gridKey
    this.omitColumns = omitColumns
    this.advancedOptions = advancedOptions
    this.advancedOptionsParamsFunc = advancedOptionsParams
    this.isCacheLoadedFunc = isCacheLoadedFunc

    this.gridApi.addEventListener(
      'cellKeyPress',
      this.onCellKeyPress.bind(this),
    )

    return this.events.asObservable()
  }

  /**
   * Cleanup
   */
  destroy() {
    this.gridApi = undefined
    this.advancedOptions = undefined
    document.removeEventListener('keypress', this.onkeyPressBinding)
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.hotkeysDestroyFn?.()
  }

  /**
   * We need a ag-grid cell keypress listener to capture the ';' keypress.
   *
   * This needs to be manual because the hotkeys.js doesn't do scoping and
   * since this service is a scoped service, we can't use the hotkeys service.
   */
  onCellKeyPress({ event }: { event: KeyboardEvent }) {
    const el = (event.target as HTMLElement) ?? {}
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      return
    }

    if (event.key === ';') {
      this.launchFiltersModal()
    }
  }

  /**
   * We need a global keypress listener to capture the ';' keypress.
   *
   * This needs to be manual because the hotkeys.js doesn't do scoping and
   * since this service is a scoped service, we can't use the hotkeys service.
   */
  onKeyPress(event: KeyboardEvent) {
    const el = (event.target as HTMLElement) ?? {}
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      return
    }

    if (event.key === ';' && this.isInFocusedPanel()) {
      this.launchFiltersModal()
    }
  }

  /**
   * Checks if the current UI is in the focused panel
   */
  isInFocusedPanel(): boolean {
    return this._focusedUIKey === this.currentPanelKey
  }

  setValidators(validators: { [field: string]: ValidatorFn }) {
    this.validators = validators
  }

  /**
   * The column-filter models the modal is built from.
   *
   * An extension point purely so a subclass can substitute an override-aware builder without
   * duplicating `launchFiltersModal` — see `AggInfoFilterModalService`. The base behaviour is exactly
   * what was inline here before.
   *
   * `gridApi` is narrowed by the caller: `launchFiltersModal` returns early when it is unset, and it
   * is the only caller.
   */
  protected buildColumnFilters(): ColumnFilterModel[] {
    return getColumnFilterModels(this.gridApi as GridApi, {
      omitColumns: this.omitColumns,
    })
  }

  /**
   * Whether the modal may offer the per-column comparison-type picker (PVAL-26423).
   *
   * FALSE here, and the only override is `AggInfoFilterModalService` — so every existing consumer of
   * this service gets a modal identical to before, with no picker in the DOM. A second extension point
   * alongside {@link buildColumnFilters} rather than a constructor flag or a public setter: the two
   * always move together (a picker with no override-aware column models would render nothing, and
   * override-aware models with no picker would offer no way to change the type), so binding both to
   * the same subclass makes the mismatched combinations unreachable rather than merely discouraged.
   */
  protected get columnTypeOverrideEnabled(): boolean {
    return false
  }

  /**
   * Open the filters modal and set the grid, also prevent multiple fires of the same modal
   */
  async launchFiltersModal() {
    if (this.isCacheLoadedFunc != null && !this.isCacheLoadedFunc()) {
      //If cache is not loaded we are not ready to filter.
      return
    }

    if (!this.gridApi || this._isModalLaunched) {
      return
    }
    this._isModalLaunched = true

    const columnFilters = this.buildColumnFilters()

    const filterModel = this.gridApi.getFilterModel()

    const compKey = 'filter-modal'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(FilterModalComponent, {
      size: 'lg',
      windowClass: `hs-filter-modal modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    })

    modalRef.componentInstance.omitColumns = this.omitColumns
    modalRef.componentInstance.editorModel = {
      columnFilters,
      filterModel,
    } as FilterEditorModel
    modalRef.componentInstance.gridApi = this.gridApi
    modalRef.componentInstance.gridKey = this.currentGridKey
    modalRef.componentInstance.advancedOptions = this.advancedOptions
    modalRef.componentInstance.enableClearFilters = this.isClearFiltersEnabled
    modalRef.componentInstance.enableResetFilters = this.isResetFiltersEnabled
    modalRef.componentInstance.enableColumnTypeOverride =
      this.columnTypeOverrideEnabled
    modalRef.componentInstance.validators = this.validators

    if (this.advancedOptionsParamsFunc) {
      modalRef.componentInstance.advancedOptionsParams =
        this.advancedOptionsParamsFunc()
    }

    // PVAL-16248: prevent modal from adding an empty line for omitted columns
    // can't add createdTimestamp column filter yet due to the northfork date/time filter bug
    if (Object.keys(filterModel).some(key => this.omitColumns.includes(key))) {
      modalRef.componentInstance.editorModel.filterModel =
        this.removeOmittedColumnsFromFilterModel(filterModel, this.omitColumns)
      this.isOmitColumnRemoved = true
    } else {
      modalRef.componentInstance.editorModel.filterModel = filterModel
      this.isOmitColumnRemoved = false
    }

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    const omittedFilters = {} as FilterModel
    this.omitColumns.forEach(column => {
      if (column in filterModel) {
        omittedFilters[column] = filterModel[column]
      }
    })

    try {
      const event = await modalRef.result
      switch (event.type) {
        case 'Apply':
          if (event.filterModel) {
            if (this.isOmitColumnRemoved) {
              // add omitted columns back to filter model when applying filters
              const combinedFilterModel = {
                ...omittedFilters,
                ...event.filterModel,
              }
              this.gridApi?.setFilterModel(combinedFilterModel)
              this.isOmitColumnRemoved = false
            } else this.gridApi?.setFilterModel(event.filterModel)
          }
          break
        case 'ClearAll':
          this.clearAllFilters()
          break
        case 'ResetAll':
          this.onResetFilters.next()
          break
      }
      this.events.next(event)
    } catch (err) {
      // ignore error
    } finally {
      this._isModalLaunched = false
    }
  }

  removeOmittedColumnsFromFilterModel(
    filterModel: FilterModel,
    omitColumns: string[],
  ): FilterModel {
    if (!Object.keys(filterModel).some(key => omitColumns.includes(key))) {
      return filterModel
    }

    const updatedFilterModel = {} as FilterModel
    for (const [key, value] of Object.entries(filterModel)) {
      if (!omitColumns.includes(key)) {
        updatedFilterModel[key] = value
      }
    }
    return updatedFilterModel
  }

  clearAllFilters() {
    this.gridApi?.setFilterModel(null)
  }
}
